/**
 * Promo Codes Service
 * Admin manages codes via backend API → stored in CosmosDB
 *   (tipoEntidad: 'promoCode', partitionKey: 'promoCode')
 *   (tipoEntidad: 'promoCodeRedemption', partitionKey: 'promoCodeRedemption')
 *
 * Redemptions are linked to Supabase user IDs.
 *
 * Set REACT_APP_PROMO_MOCK=true to use localStorage mock (dev without backend).
 */

import { API_BASE_URL } from '../config';
import apiClient from './apiClient';

const USE_MOCK = process.env.REACT_APP_PROMO_MOCK === 'true';
const PROMO_API_URL = `${API_BASE_URL}/promo-codes`;
const PENDING_REGISTRATION_PROMO_KEY = 'soccertycs.pendingRegistrationPromo.v1';
const PENDING_REGISTRATION_PROMO_TTL_MS = 7 * 24 * 60 * 60 * 1000;
let pendingRegistrationRedemption = null;

// ─── Duration options (shared between admin UI and register form) ─────────────

export const PROMO_DURATION_OPTIONS = [
  { value: 1,  label: '1 mes' },
  { value: 3,  label: '3 meses' },
  { value: 6,  label: '6 meses' },
  { value: 12, label: '1 año' },
];

export const PROMO_LICENSE_OPTIONS = [
  { value: '', label: 'Cualquier licencia individual activa' },
  { value: 'trial-full', label: 'Acceso completo' },
  { value: 'coach-web', label: 'Coach Web' },
  { value: 'coach-team', label: 'Coach Team' },
  { value: 'coach-suite', label: 'Coach Suite' },
  { value: 'analyst-web', label: 'Analyst Web' },
  { value: 'analyst-team', label: 'Analyst Team' },
  { value: 'analyst-suite', label: 'Analyst Suite' },
  { value: 'performance-web', label: 'Performance Web' },
  { value: 'performance-team', label: 'Performance Team' },
  { value: 'performance-suite', label: 'Performance Suite' },
];

export const getPromoLicenseLabel = (planKey) =>
  PROMO_LICENSE_OPTIONS.find((option) => option.value === (planKey || ''))?.label
  || planKey
  || 'Cualquier licencia individual activa';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

export function storePendingRegistrationPromo({ code, userId, userEmail }) {
  const pendingPromo = {
    code: String(code || '').trim().toUpperCase(),
    userId: String(userId || '').trim(),
    userEmail: normalizeEmail(userEmail),
    createdAt: Date.now(),
  };
  if (!pendingPromo.code || !pendingPromo.userId || !pendingPromo.userEmail) {
    throw new Error('No se pudo guardar el código promocional pendiente.');
  }
  sessionStorage.setItem(PENDING_REGISTRATION_PROMO_KEY, JSON.stringify(pendingPromo));
  return pendingPromo;
}

export function getPendingRegistrationPromo() {
  try {
    const pendingPromo = JSON.parse(sessionStorage.getItem(PENDING_REGISTRATION_PROMO_KEY) || 'null');
    if (
      !pendingPromo?.code
      || !pendingPromo?.userId
      || !pendingPromo?.userEmail
      || !Number.isFinite(pendingPromo?.createdAt)
      || Date.now() - pendingPromo.createdAt > PENDING_REGISTRATION_PROMO_TTL_MS
    ) {
      sessionStorage.removeItem(PENDING_REGISTRATION_PROMO_KEY);
      return null;
    }
    return pendingPromo;
  } catch {
    sessionStorage.removeItem(PENDING_REGISTRATION_PROMO_KEY);
    return null;
  }
}

export async function redeemPendingRegistrationPromo(session) {
  const pendingPromo = getPendingRegistrationPromo();
  if (!pendingPromo || !session?.user) return null;
  if (
    pendingPromo.userId !== session.user.id
    || pendingPromo.userEmail !== normalizeEmail(session.user.email)
  ) {
    return null;
  }

  if (pendingRegistrationRedemption) return pendingRegistrationRedemption;
  pendingRegistrationRedemption = redeemPromoCode({
    code: pendingPromo.code,
    userId: pendingPromo.userId,
    userEmail: pendingPromo.userEmail,
    accessToken: session.access_token,
  })
    .then((result) => {
      sessionStorage.removeItem(PENDING_REGISTRATION_PROMO_KEY);
      return result;
    })
    .finally(() => {
      pendingRegistrationRedemption = null;
    });
  return pendingRegistrationRedemption;
}

// ─── API helper ───────────────────────────────────────────────────────────────

async function req(url, options = {}) {
  try {
    const { headers, ...requestOptions } = options;
    const response = await apiClient.request({
      url,
      headers: { 'Content-Type': 'application/json', ...headers },
      ...requestOptions,
    });
    return response.data;
  } catch (error) {
    const payload = error.response?.data;
    const requestError = new Error(payload?.error || payload?.message || `Error HTTP ${error.response?.status || 500}`);
    requestError.details = payload;
    throw requestError;
  }
}

// ─── Mock (localStorage) fallback ─────────────────────────────────────────────

const MOCK_CODES_KEY = 'soccerAnalytics_promoCodes';
const MOCK_REDEMPTIONS_KEY = 'soccerAnalytics_promoRedemptions';

function mockLoad(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
}
function mockSave(key, data) { localStorage.setItem(key, JSON.stringify(data)); }
function mockId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Admin API ────────────────────────────────────────────────────────────────

/**
 * List all promo codes with usage count (admin only).
 * @returns {Promise<Array>}
 */
export async function getPromoCodes() {
  if (USE_MOCK) {
    await delay(200);
    const codes = mockLoad(MOCK_CODES_KEY);
    const redemptions = mockLoad(MOCK_REDEMPTIONS_KEY);
    return codes.map((c) => ({ ...c, usedCount: redemptions.filter((r) => r.promoCodeId === c.id).length }));
  }
  const data = await req(`${PROMO_API_URL}/admin`);
  return data.codes ?? data ?? [];
}

/**
 * Create a new promo code (admin only).
 * CosmosDB doc: { tipoEntidad: 'promoCode', partitionKey: 'promoCode', ... }
 * @param {{ code, discountPercent, durationMonths, maxUses, expiresAt }} params
 * @returns {Promise<object>}
 */
export async function createPromoCode({
  code,
  discountPercent,
  durationMonths,
  maxUses,
  expiresAt,
  targetPlanKey,
}) {
  const normalized = code.trim().toUpperCase();
  if (!normalized) throw new Error('El código no puede estar vacío');
  if (discountPercent < 0 || discountPercent > 100) throw new Error('El descuento debe estar entre 0% y 100%');
  if (!PROMO_DURATION_OPTIONS.some((d) => d.value === Number(durationMonths))) throw new Error('Duración no válida');

  if (USE_MOCK) {
    await delay(300);
    const codes = mockLoad(MOCK_CODES_KEY);
    if (codes.some((c) => c.code === normalized)) throw new Error('Ya existe un código con ese nombre');
    const doc = {
      id: mockId(),
      tipoEntidad: 'promoCode',
      partitionKey: 'promoCode',
      code: normalized,
      discountPercent: Number(discountPercent),
      durationMonths: Number(durationMonths),
      maxUses: maxUses != null && maxUses !== '' ? Number(maxUses) : null,
      targetPlanKey: targetPlanKey || null,
      active: true,
      createdAt: new Date().toISOString(),
      expiresAt: expiresAt || null,
    };
    codes.push(doc);
    mockSave(MOCK_CODES_KEY, codes);
    return doc;
  }

  return req(`${PROMO_API_URL}/admin`, {
    method: 'POST',
    data: {
      code: normalized,
      discountPercent: Number(discountPercent),
      durationMonths: Number(durationMonths),
      maxUses: maxUses || null,
      expiresAt: expiresAt || null,
      targetPlanKey: targetPlanKey || null,
    },
  });
}

/**
 * Toggle active status of a promo code (admin only).
 * @param {string} id
 * @param {boolean} active
 * @returns {Promise<object>}
 */
export async function togglePromoCode(id, active) {
  if (USE_MOCK) {
    await delay(200);
    const codes = mockLoad(MOCK_CODES_KEY);
    const idx = codes.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Código no encontrado');
    codes[idx] = { ...codes[idx], active };
    mockSave(MOCK_CODES_KEY, codes);
    return codes[idx];
  }
  return req(`${PROMO_API_URL}/admin/${encodeURIComponent(id)}`, { method: 'PATCH', data: { active } });
}

/**
 * Update the editable limits of a promo code (admin only).
 * @param {string} id
 * @param {{ maxUses: number|null, expiresAt: string|null }} changes
 * @returns {Promise<object>}
 */
export async function updatePromoCode(id, { maxUses, expiresAt }) {
  const normalizedMaxUses = maxUses == null || maxUses === '' ? null : Number(maxUses);
  if (normalizedMaxUses !== null && (!Number.isInteger(normalizedMaxUses) || normalizedMaxUses < 1)) {
    throw new Error('El número máximo de usos debe ser un entero mayor que cero');
  }

  const changes = {
    maxUses: normalizedMaxUses,
    expiresAt: expiresAt || null,
  };

  if (USE_MOCK) {
    await delay(200);
    const codes = mockLoad(MOCK_CODES_KEY);
    const idx = codes.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Código no encontrado');
    codes[idx] = { ...codes[idx], ...changes };
    mockSave(MOCK_CODES_KEY, codes);
    return codes[idx];
  }

  const data = await req(`${PROMO_API_URL}/admin/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: changes,
  });
  return data.promoCode ?? data;
}

/**
 * Delete a promo code (admin only).
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function deletePromoCode(id) {
  if (USE_MOCK) {
    await delay(200);
    mockSave(MOCK_CODES_KEY, mockLoad(MOCK_CODES_KEY).filter((c) => c.id !== id));
    return;
  }
  await req(`${PROMO_API_URL}/admin/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ─── User API ─────────────────────────────────────────────────────────────────

/**
 * Preview a promo code without redeeming (real-time UI feedback at registration).
 * Does NOT require authentication.
 * @param {string} code
 * @returns {Promise<{ durationMonths, discountPercent, durationLabel }|null>}
 */
export async function previewPromoCode(code) {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return null;

  if (USE_MOCK) {
    await delay(300);
    const promoCode = mockLoad(MOCK_CODES_KEY).find((c) => c.code === normalized);
    if (!promoCode || !promoCode.active) return null;
    if (promoCode.expiresAt && new Date(promoCode.expiresAt) < new Date()) return null;
    const durationLabel = PROMO_DURATION_OPTIONS.find((d) => d.value === promoCode.durationMonths)?.label ?? `${promoCode.durationMonths} meses`;
    return { durationMonths: promoCode.durationMonths, discountPercent: promoCode.discountPercent, durationLabel };
  }

  try {
    const data = await req(`${PROMO_API_URL}/preview/${encodeURIComponent(normalized)}`, { skipAuth: true });
    if (!data) return null;
    const durationLabel = PROMO_DURATION_OPTIONS.find((d) => d.value === data.durationMonths)?.label ?? `${data.durationMonths} meses`;
    return { ...data, durationLabel };
  } catch {
    return null;
  }
}

/**
 * Redeem a promo code at registration.
 * The Supabase user ID is stored in the CosmosDB redemption document
 * so that the backend can link the benefit to the user's profile/subscription.
 *
 * @param {{ code: string, userId: string, userEmail: string, confirmPlanChange?: boolean }} params
 * @returns {Promise<{ durationMonths, discountPercent, durationLabel }>}
 */
export async function redeemPromoCode({
  code,
  userId,
  userEmail,
  confirmPlanChange = false,
  accessToken,
}) {
  const normalized = code.trim().toUpperCase();

  if (USE_MOCK) {
    await delay(400);
    const codes = mockLoad(MOCK_CODES_KEY);
    const redemptions = mockLoad(MOCK_REDEMPTIONS_KEY);
    const promoCode = codes.find((c) => c.code === normalized);
    if (!promoCode) throw new Error('Código promocional no válido');
    if (!promoCode.active) throw new Error('Este código ya no está activo');
    if (promoCode.expiresAt && new Date(promoCode.expiresAt) < new Date()) throw new Error('Este código ha expirado');
    const usedCount = redemptions.filter((r) => r.promoCodeId === promoCode.id).length;
    if (promoCode.maxUses != null && usedCount >= promoCode.maxUses) throw new Error('Este código ha alcanzado el límite de usos');
    if (redemptions.some((r) => r.promoCodeId === promoCode.id && r.userId === userId)) throw new Error('Ya has usado este código');
    const redemption = {
      id: mockId(),
      tipoEntidad: 'promoCodeRedemption',
      partitionKey: 'promoCodeRedemption',
      promoCodeId: promoCode.id,
      code: normalized,
      userId,
      userEmail,
      redeemedAt: new Date().toISOString(),
    };
    redemptions.push(redemption);
    mockSave(MOCK_REDEMPTIONS_KEY, redemptions);
    const durationLabel = PROMO_DURATION_OPTIONS.find((d) => d.value === promoCode.durationMonths)?.label ?? `${promoCode.durationMonths} meses`;
    return { durationMonths: promoCode.durationMonths, discountPercent: promoCode.discountPercent, durationLabel };
  }

  const data = await req(`${PROMO_API_URL}/redeem`, {
    method: 'POST',
    data: { code: normalized, userId, userEmail, confirmPlanChange },
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
  const durationLabel = PROMO_DURATION_OPTIONS.find((d) => d.value === data.durationMonths)?.label ?? `${data.durationMonths} meses`;
  return { ...data, durationLabel };
}
